import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, ShieldCheck, HardHat } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABEL = { admin: "Admin", usta: "Usta", atolye: "Atölye Tableti" };
const capitalize = (s) => (s ? s.charAt(0).toLocaleUpperCase("tr") + s.slice(1) : s);
const roleLabel = (r) => ROLE_LABEL[r] || capitalize(r);

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [workerRoles, setWorkerRoles] = useState(["usta"]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ id: null, name: "", email: "", password: "", role: "usta" });
  const [delOpen, setDelOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) { toast.error("Kullanıcılar yüklenemedi"); }
  };
  useEffect(() => {
    load();
    api.get("/settings").then(({ data }) => {
      if (Array.isArray(data.worker_roles) && data.worker_roles.length) setWorkerRoles(data.worker_roles);
    }).catch(() => {});
  }, []);

  const openNew = () => {
    setForm({ id: null, name: "", email: "", password: "", role: workerRoles[0] || "usta" });
    setOpen(true);
  };
  const openEdit = (u) => {
    setForm({ id: u.id, name: u.name, email: u.email, password: "", role: u.role });
    setOpen(true);
  };
  const save = async () => {
    try {
      if (form.id) {
        const body = { name: form.name, role: form.role };
        if (form.password) body.password = form.password;
        await api.put(`/users/${form.id}`, body);
        toast.success("Kullanıcı güncellendi");
      } else {
        if (!form.email || !form.password || !form.name) {
          toast.error("Tüm alanları doldurun"); return;
        }
        await api.post("/users", form);
        toast.success("Kullanıcı oluşturuldu");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/users/${delTarget.id}`);
      toast.success("Kullanıcı silindi");
      setDelOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500">Yönetim</div>
          <h1 className="text-3xl mt-1">Kullanıcılar</h1>
        </div>
        <Button data-testid="new-user-button" onClick={openNew} className="bg-zinc-900 hover:bg-zinc-800">
          <Plus size={16} className="mr-1" />Yeni Kullanıcı
        </Button>
      </div>

      <div className="bg-white rounded-md border border-zinc-200 overflow-hidden">
        <table className="acc">
          <thead>
            <tr><th>Ad</th><th>E-posta</th><th>Rol</th><th>Oluşturulma</th><th style={{width:100}}></th></tr>
          </thead>
          <tbody>
            {users.length === 0 && <tr><td colSpan={5} className="text-center text-zinc-500 py-8">Kullanıcı yok</td></tr>}
            {users.map(u => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="font-medium flex items-center gap-2">
                  {u.role === "admin"
                    ? <ShieldCheck size={14} className="text-emerald-600" />
                    : <HardHat size={14} className="text-amber-600" />}
                  {u.name}
                  {u.id === me?.id && <span className="text-xs text-zinc-400">(siz)</span>}
                </td>
                <td className="text-zinc-600">{u.email}</td>
                <td>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs border ${u.role === "admin" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                    {roleLabel(u.role)}
                  </span>
                </td>
                <td className="text-zinc-500 text-sm">{u.created_at?.slice(0,10)}</td>
                <td className="flex gap-1">
                  <button data-testid={`edit-user-${u.id}`} onClick={()=>openEdit(u)} className="text-zinc-400 hover:text-zinc-900"><Pencil size={14}/></button>
                  {u.id !== me?.id && (
                    <button data-testid={`delete-user-${u.id}`} onClick={()=>{ setDelTarget(u); setDelOpen(true); }} className="text-zinc-400 hover:text-red-600"><Trash2 size={14}/></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-form-dialog">
          <DialogHeader><DialogTitle>{form.id ? "Kullanıcıyı Düzenle" : "Yeni Kullanıcı"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Ad Soyad</Label><Input data-testid="user-name-input" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} /></div>
            <div className="col-span-2"><Label>E-posta</Label><Input data-testid="user-email-input" disabled={!!form.id} type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} /></div>
            <div className="col-span-2">
              <Label>Şifre {form.id && <span className="text-xs text-zinc-400">(boş bırakılırsa değişmez)</span>}</Label>
              <Input data-testid="user-password-input" type="password" value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} />
            </div>
            <div className="col-span-2">
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v)=>setForm({...form, role: v})}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {workerRoles.map((r) => (
                    <SelectItem key={r} value={r}>{roleLabel(r)} (sadece aktif projeler & ürünler)</SelectItem>
                  ))}
                  <SelectItem value="atolye">Atölye Tableti (usta seçmeli ortak hesap)</SelectItem>
                  <SelectItem value="admin">Admin (tüm yetkiler)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setOpen(false)}>İptal</Button>
            <Button data-testid="user-save-button" onClick={save} className="bg-zinc-900 hover:bg-zinc-800">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kullanıcı silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              {delTarget?.name} ({delTarget?.email}) silinecek. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-user" onClick={doDelete} className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
